import React from 'react'
import _ from 'lodash'
import Router from 'next/router'

import { Sentry } from '../../Sentry'

const METHOD_NEEDLE_SSR_OR_PRERENDER =
  '__AL_EX_needleIsSsrOrPrerender_lHMjAMj4p9jJAnxO'
const HAS_ROUTE_CHANGE_COMPLETE =
  '__AL_EX_HAS_ROUTE_CHANGE_COMPLETE_lHMjAMj4p9jJAnxO'

export default function withRoot(App) {
  return class extends React.PureComponent {
    static displayName = `fwAppWithRoot(${
      App.displayName || App.name || 'App'
    })`

    // SSR 页，在服务端执行，客户端不执行
    // CSR 时，首页不执行（直接使用SSR推导状态），其他页执行
    static async getInitialProps(appCtx) {
      // 环境变量支持
      const env = appCtx.ctx.__AL_EX_internalScope.globalConfig.env(
        appCtx.ctx,
        undefined
      )
      appendCtx(appCtx.ctx, { env })

      // 扩展生命周期
      const lifecycleProps = await appCtx.ctx.__AL_EX_internalScope.callGlobalLifecycle(
        appCtx.ctx,
        {
          isModule: false,
          isApp: true,
          isPage: false,
          isInstance: false,
        }
      )

      // 完成 next 的 系统生命周期
      // 执行前，确保 ctx/internalScope 已就绪
      let initialProps = {}
      if ('getInitialProps' in App) {
        initialProps = await App.getInitialProps(appCtx)
      }

      // 已发生错误时，提前返回。不再执行自定义行为（产生更多的异常错误，更难处理）
      // TODO 严格做异常分支测试，现行异常流程基本不对
      if (
        appCtx.ctx.err ||
        (initialProps.pageProps && 'statusCode' in initialProps.pageProps) ||
        appCtx.ctx.router.route === '/_error'
      ) {
        return {
          ...initialProps,
          __AL_EX_ExtraInitialState:
            appCtx.ctx.__AL_EX_internalScope.computeExtraInitialState,
          __AL_EX_ExtraDocProps: appCtx.ctx.__AL_EX_internalScope.extraDocProps,
          __AL_EX_ExtraDocPageProps:
            appCtx.ctx.__AL_EX_internalScope.extraDocPageProps,
        }
      }

      if (
        !appCtx.ctx.lifecycleStatus.isExport &&
        initialProps.pageProps.__AL_EX_PAGE_IDENTITY_Ia4TZzV7F1x12Jp3 !==
          'd3b5fa5f-860d-4d66-8cf6-594e21a72873'
      ) {
        // 页面未使用 withPage 包装
        if (appCtx.ctx.__AL_EX_internalScope.globalConfig.extendsPageConfig) {
          if (process.dev) {
            console.warn(
              '业务工程提供了 extendsPageConfig，但页面未使用 withPage 包装。extendsPageConfig 在当前页面未实际渲染'
            )
          }
        }
      }

      if (
        !(
          process.server ||
          (process.browser &&
            !appCtx.ctx.__AL_EX_internalScope.lifecycleStatus.isFirstPage)
        )
      ) {
        throw new Error(
          '框架时序BUG。app的with系列getInitialProps，实际仅在 server、client非首页 执行'
        )
      }

      // 注意，以 __AL_EX_ 开头的项 将被过滤(render.tsx)，不传递至 __NEXT_DATA__，browser 无法取到
      // 注意，此处不能直接使用 _.merge，将导致对象引用改变，必须手动合并
      return {
        ...mergeProps(appCtx.ctx, lifecycleProps, initialProps),
        initialEnv: env,
        __AL_EX_ExtraInitialState:
          appCtx.ctx.__AL_EX_internalScope.computeExtraInitialState,
        __AL_EX_ExtraDocProps: appCtx.ctx.__AL_EX_internalScope.extraDocProps,
        __AL_EX_ExtraDocPageProps:
          appCtx.ctx.__AL_EX_internalScope.extraDocPageProps,
        [METHOD_NEEDLE_SSR_OR_PRERENDER]: () => {}, // 仅 csr 时能传递到 constructor
      }
    }

    // 无论 server、browser 均执行
    // getInitialProps 结果将作为 props 传入
    // 但 browser 初始化 ssr 渲染页面时，仅包含可序列化状态，缺少各复杂对象，需手动重建
    constructor(props, ...rest) {
      super(props, ...rest)

      // 初始化 env
      const env = props.ctx.__AL_EX_internalScope.globalConfig.env(
        props.ctx,
        props.initialEnv
      )
      appendCtx(props.ctx, { env })

      const pageLifecycleProps = props.ctx.__AL_EX_internalScope.callGlobalLifecycle(
        props.ctx,
        {
          isModule: false,
          isApp: true,
          isPage: false,
          isInstance: true,
        }
      )
      this.state = { pageLifecycleProps: pageLifecycleProps }

      // 更新页面语言，行为与 _document 保持一致
      if (process.browser && props.ctx.lifecycleStatus.isFirstPage) {
        const documentEl = document.documentElement
        if (!documentEl.getAttribute('lang') && props.ctx.i18n.language) {
          documentEl.setAttribute('lang', props.ctx.i18n.language)
        }
      }

      this.handleRouteChangeStart = this.handleRouteChangeStart.bind(this)
      this.handleRouteChangeError = this.handleRouteChangeError.bind(this)

      if (process.browser) {
        Router.events.on('routeChangeStart', this.handleRouteChangeStart)
        Router.events.on('routeChangeError', this.handleRouteChangeError)
      }
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
      if (process.browser) {
        // 页面切换时，App 不会重建（在路由之上）
        if (this.props.Component !== nextProps.Component) {
          const pageLifecycleProps = nextProps.ctx.__AL_EX_internalScope.callGlobalLifecycle(
            this.props.ctx,
            {
              isModule: false,
              isApp: true,
              isPage: false,
              isInstance: true,
            }
          )
          this.setState({ pageLifecycleProps })
        }
      }
    }

    componentDidCatch(error, errorInfo) {
      if (process.browser) {
        // 注意，业务自定义 _app 时，需再次实现 sentry 在 didCatch 采集错误
        const isUseSentry = !!this.props.ctx.__AL_EX_internalScope.runtimeConfig
          .frameworkConfig.sentry.enabled
        if (isUseSentry) {
          Sentry.withScope((scope) => {
            scope.setExtras(errorInfo)
            Sentry.captureException(error)
          })
        }
      }
      throw error
    }

    componentWillUnmount() {
      Router.events.off('routeChangeStart', this.handleRouteChangeStart)
      Router.events.off('routeChangeComplete', this.handleRouteChangeComplete)
      Router.events.off('routeChangeError', this.handleRouteChangeError)
    }

    handleRouteChangeStart() {
      // 路由变化时，必然不是第一页
      // 注意，路由切换是一个过程，而非瞬时的动作
      // 此处，从路由变化开始，视为非首页，以尽可能保证逻辑正确
      this.props.ctx.__AL_EX_internalScope.updateIsFirstPage(false)
    }

    handleRouteChangeComplete() {
      // 标记曾路由切换成功
      window[HAS_ROUTE_CHANGE_COMPLETE] = true
      Router.events.off('routeChangeStart', this.handleRouteChangeStart)
      Router.events.off('routeChangeComplete', this.handleRouteChangeComplete)
      Router.events.off('routeChangeError', this.handleRouteChangeError)
    }

    handleRouteChangeError() {
      // 路由切换错误，恢复此前的状态
      // 可能存在一些风险，框架和业务代码均未考虑，变为非首页、又再变为首页的场景
      this.props.ctx.__AL_EX_internalScope.updateIsFirstPage(
        !!window[HAS_ROUTE_CHANGE_COMPLETE]
      )
    }

    render() {
      const { props } = this
      const { ctx } = props

      // 注入 ctx 至 pageProps，pageProps 将传递至 page
      // 注意，此处不能直接使用 _.merge，将导致对象引用改变，必须手动合并
      let newProps = mergeProps(ctx, this.state.pageLifecycleProps, this.props)
      newProps.pageProps.ctx = ctx

      // 仅传递必要的有限 props，防止滥用
      // 按 _app.ts 定义返回
      newProps = _.pick(newProps, [
        'ctx',
        // NextJs 默认提供，保持一致
        'Component',
        'initialState',
        'pageProps',
        'router',
        'err',
        '__N_SSG',
        '__N_SSP',
      ])
      // 过滤 __AL_EX_ 开头的 prop，防止不必要的公开至 client
      newProps.pageProps = _.omitBy(newProps.pageProps, (v, k) =>
        k.startsWith('__AL_EX_')
      )

      // export 和 shouldPrerenderOnExportTarget，已在 ExportCompatibility 处理
      return <App {...newProps} />
    }
  }

  function mergeProps(ctx, pageLifecycleProps, props) {
    if (
      typeof ctx.__AL_EX_internalScope.then === 'function' ||
      typeof ctx.__AL_EX_internalScope.initialModuleLifecycleProps.then ===
        'function'
    ) {
      throw new Error('框架生命周期异常')
    }
    return {
      ...ctx.__AL_EX_internalScope.initialModuleLifecycleProps,
      ...pageLifecycleProps,
      ...props,
      pageProps: {
        ...(ctx.__AL_EX_internalScope.initialModuleLifecycleProps &&
          ctx.__AL_EX_internalScope.initialModuleLifecycleProps.pageProps),
        ...(pageLifecycleProps && pageLifecycleProps.pageProps),
        ...(props && props.pageProps),
      },
    }
  }

  function appendCtx(ctx, { env }) {
    ctx.env = env
  }
}
